import { computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { distinctUntilChanged, filter, map, take, type Subscription } from 'rxjs';

import { environment } from '@env';

import { AppPath, type HttpsUrl, type RouteTitle, type RouteUrl } from '@kernel';

import { AuthStore } from '@core/auth';
import { LogService } from '@core/log';
import { SESSION_STORAGE, SESSION_STORAGE_KEYS } from '@core/storage';

import { NavCacheService } from './nav-cache.service';
import { NavRouteService } from './nav-route.service';
import { createMenuItems, MAX_TABS, TAB_EXCLUDED_PATHS } from './nav.config';
import {
  DEFAULT_TAB_TITLE,
  type MenuItem,
  type NavContext,
  NavIntent,
  NavMode,
  type QueryParams,
  type Tab,
  type TabBase,
} from './nav.types';
import { findLeafSnapshot, findRootSnapshot, resolveRouteTitle } from './nav.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isTabBase = (value: unknown): value is TabBase =>
  isRecord(value) &&
  typeof value['key'] === 'string' &&
  typeof value['isRoot'] === 'boolean' &&
  typeof value['title'] === 'string' &&
  typeof value['routeUrl'] === 'string';

const isTab = (value: unknown): value is Tab => {
  if (!isRecord(value)) return false;

  const id = value['id'];
  const history = value['history'];

  return (
    isTabBase(value) &&
    typeof id === 'string' &&
    Array.isArray(history) &&
    history.length > 0 &&
    history.every(isTabBase)
  );
};

const generateTabId = (): string => {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2, 11);

  return `tab-${Date.now()}-${uuid}`;
};

interface NavState {
  readonly tabs: readonly Tab[];
  readonly activeTabId: string;
  readonly tabLimitAttempts: number;
  readonly inboxCount: number;
  readonly conversationsCount: number;
  readonly menuItems: readonly MenuItem[];
}

export const NavStore = signalStore(
  { providedIn: 'root' },

  environment.withDevtools('nav'),

  withState<NavState>({
    tabs: [],
    activeTabId: '',
    tabLimitAttempts: 0,
    inboxCount: 0,
    conversationsCount: 0,
    menuItems: [],
  }),

  withComputed((store) => ({
    activeTab: computed(() => store.tabs().find((t) => t.id === store.activeTabId()) ?? null),
    maxTabsReached: computed(() => store.tabs().length >= MAX_TABS),
  })),

  withMethods((store) => {
    const router = inject(Router);
    const authStore = inject(AuthStore);
    const navRouteService = inject(NavRouteService);
    const navCacheService = inject(NavCacheService);
    const session = inject(SESSION_STORAGE);
    const log = inject(LogService);

    let navEndSubscription: Subscription | null = null;
    let lifecycleId = 0;
    let userId: number | null = null;

    const setTabs = (tabs: readonly Tab[]): void => {
      patchState(store, { tabs });
      session.setJSON(SESSION_STORAGE_KEYS.nav.tabs, tabs);
    };

    const setActiveTabId = (activeTabId: string): void => {
      patchState(store, { activeTabId });
      session.setJSON(SESSION_STORAGE_KEYS.nav.activeTabId, activeTabId);

      if (activeTabId) navRouteService.stampCurrentTab(activeTabId);
    };

    const notifyTabLimit = (): void => {
      patchState(store, { tabLimitAttempts: store.tabLimitAttempts() + 1 });
    };

    const createTabBase = (): TabBase | null => {
      const leafSnapshot = findLeafSnapshot(router.routerState.snapshot.root);
      const fullKeyObj = navRouteService.createKeyFromSnapshot(leafSnapshot);
      const identity = navRouteService.createTabIdentityFromSnapshot(leafSnapshot);

      if (!fullKeyObj || !identity) return null;

      const titleSnapshot = identity.isRoot ? findRootSnapshot(leafSnapshot) : leafSnapshot;
      const { title, titleParams }: RouteTitle = resolveRouteTitle(
        titleSnapshot,
        store.menuItems(),
      ) ?? { title: DEFAULT_TAB_TITLE };

      return {
        ...identity,
        title,
        ...(titleParams ? { titleParams } : {}),
        routeUrl: navRouteService.getKeyUrl(fullKeyObj),
      };
    };

    const createTab = (id: string, base: TabBase, history: readonly TabBase[]): Tab => ({
      id,
      ...base,
      history,
    });

    const replaceTab = (id: string, tab: Tab): void => {
      setTabs(store.tabs().map((t) => (t.id === id ? tab : t)));
    };

    const updateTabRoute = (tab: Tab, base: TabBase): void => {
      replaceTab(
        tab.id,
        createTab(
          tab.id,
          base,
          tab.history.map((t) => (t.key === tab.key ? base : t)),
        ),
      );
    };

    const isExcludedPath = (url: RouteUrl): boolean => {
      const keyObj = navRouteService.createKey(url);

      if (!keyObj) return true;

      return TAB_EXCLUDED_PATHS.some((p) => keyObj.path === p);
    };

    const hasTabKey = (key: string): boolean => store.tabs().some((t) => t.key === key);

    const getTabById = (id: string): Tab | null => store.tabs().find((t) => t.id === id) ?? null;

    const removeTabById = (id: string): void => {
      navCacheService.delete(id);
      setTabs(store.tabs().filter((t) => t.id !== id));
    };

    const getTabByKey = (key: string): Tab | null => {
      const activeTab = store.activeTab();

      return activeTab?.key === key ? activeTab : (store.tabs().find((t) => t.key === key) ?? null);
    };

    const getTabByUrl = (url: RouteUrl): Tab | null => {
      const activeTab = store.activeTab();

      if (activeTab && navRouteService.matchesTabUrl(activeTab, url)) return activeTab;

      return store.tabs().find((t) => navRouteService.matchesTabUrl(t, url)) ?? null;
    };

    const recoverFailedActivation = async (
      failedTabId: string,
      previousTab: Tab | null,
    ): Promise<void> => {
      const failedTab = getTabById(failedTabId);

      if (failedTab) removeTabById(failedTab.id);

      const fallbackTab =
        (previousTab ? getTabById(previousTab.id) : null) ??
        store.activeTab() ??
        store.tabs()[0] ??
        null;

      if (!fallbackTab || navRouteService.matchesTabUrl(fallbackTab, router.url)) return;

      const restored = await navRouteService.navigateByUrl(fallbackTab.routeUrl, {
        intent: NavIntent.TabSwitch,
        replaceUrl: true,
        tabId: fallbackTab.id,
      });

      if (restored && navRouteService.matchesTabUrl(fallbackTab, router.url)) {
        setActiveTabId(fallbackTab.id);
      }
    };

    const activateTabRoute = async (tabId: string, requestedUrl?: RouteUrl): Promise<boolean> => {
      const tab = getTabById(tabId);
      const previousTab = store.activeTab();

      if (!tab) return false;

      const needsStagedChildNav =
        tab.isRoot &&
        !!requestedUrl &&
        requestedUrl !== tab.routeUrl &&
        navCacheService.has(tab.id);

      const initialUrl = requestedUrl && !needsStagedChildNav ? requestedUrl : tab.routeUrl;
      const initialIntent =
        store.activeTabId() === tab.id ? NavIntent.InPlace : NavIntent.TabSwitch;
      const attached = await navRouteService.navigateByUrl(initialUrl, {
        intent: initialIntent,
        tabId: tab.id,
      });

      if (!attached) return false;

      const attachedTab = getTabById(tab.id);

      if (!attachedTab || !navRouteService.matchesTabUrl(tab, router.url)) {
        await recoverFailedActivation(tab.id, previousTab);

        return false;
      }

      const attachedBase = createTabBase();

      if (
        attachedBase &&
        attachedBase.key === attachedTab.key &&
        attachedBase.routeUrl !== attachedTab.routeUrl
      ) {
        updateTabRoute(attachedTab, attachedBase);
      }

      setActiveTabId(attachedTab.id);

      if (!needsStagedChildNav || !requestedUrl) return true;

      const navigated = await navRouteService.navigateByUrl(requestedUrl, {
        intent: NavIntent.InPlace,
        tabId: tab.id,
      });

      if (!navigated || !navRouteService.matchesTabUrl(tab, router.url)) {
        await recoverFailedActivation(tab.id, previousTab);

        return false;
      }

      const currentTab = getTabById(tab.id);
      const base = createTabBase();

      if (!currentTab || !base || base.key !== currentTab.key) return false;

      updateTabRoute(currentTab, base);

      return true;
    };

    const activateTab = (tabId: string): void => void activateTabRoute(tabId);

    const openNewTab = async (routeUrl: RouteUrl): Promise<boolean> => {
      const existing = getTabByUrl(routeUrl);

      if (existing) {
        return activateTabRoute(existing.id, existing.isRoot ? routeUrl : undefined);
      }

      if (store.maxTabsReached()) {
        notifyTabLimit();

        return false;
      }

      const tabId = generateTabId();
      const operationId = lifecycleId;
      const navigated = await navRouteService.navigateByUrl(routeUrl, {
        intent: NavIntent.TabSwitch,
        tabId,
      });

      if (!navigated) return false;
      if (operationId !== lifecycleId || !authStore.isAuthenticated()) return false;

      const base = createTabBase();

      if (!base || isExcludedPath(base.routeUrl) || !navRouteService.matchesTabUrl(base, routeUrl))
        return false;

      const newTab = createTab(tabId, base, [base]);

      setTabs([...store.tabs(), newTab]);
      setActiveTabId(newTab.id);

      return true;
    };

    const replaceActiveTab = async (routeUrl: RouteUrl, save = false): Promise<void> => {
      const activeTab = store.activeTab();

      if (!activeTab) return;

      const currentIdentity = navRouteService.createTabIdentityFromSnapshot(
        findLeafSnapshot(router.routerState.snapshot.root),
      );

      if (!currentIdentity || currentIdentity.key !== activeTab.key) return;

      const navigated = await navRouteService.navigateByUrl(routeUrl, {
        intent: NavIntent.InPlace,
        tabId: activeTab.id,
      });

      if (!navigated) return;
      if (store.activeTabId() !== activeTab.id || !getTabById(activeTab.id)) return;

      const base = createTabBase();

      if (!base) return;

      const updatedTab = createTab(activeTab.id, base, [...(save ? activeTab.history : []), base]);

      replaceTab(activeTab.id, updatedTab);
    };

    const openTab = (routeUrl: RouteUrl, mode: NavMode, save = false): void => {
      const url = navRouteService.normalizeUrl(routeUrl);

      if (!url) return;

      switch (mode) {
        case NavMode.Tab:
          return void openNewTab(url);

        case NavMode.Window: {
          if (typeof window === 'undefined') return;

          const absoluteUrl = `${window.location.origin}${url}` as HttpsUrl;

          return navRouteService.openExternalLink(absoluteUrl);
        }

        case NavMode.Replace:
          return void replaceActiveTab(url, save);

        default: {
          log.warn(`[NavStore] Unsupported mode, using "${NavMode.Tab}"`);
          void openNewTab(url);
        }
      }
    };

    const openLandingTab = (): void => {
      const landingPath = authStore.user()?.preferences?.landingPath ?? AppPath.Dashboard;

      const findItem = (items: readonly MenuItem[], path: string): MenuItem | undefined => {
        for (const item of items) {
          if (item.path === path) return item;

          if (item.children?.length) {
            const found = findItem(item.children, path);

            if (found) return found;
          }
        }

        return undefined;
      };

      const item =
        findItem(store.menuItems(), landingPath) ?? findItem(store.menuItems(), AppPath.Dashboard);

      if (!item?.path) return;

      openTab(item.path, NavMode.Tab);
    };

    const closeTabRoute = async (tabId: string): Promise<void> => {
      const tabs = store.tabs();

      if (tabs.length < 2) return;

      const idx = tabs.findIndex((t) => t.id === tabId);

      if (idx === -1) return;

      const tab = tabs[idx]!;
      const remainingTabs = tabs.filter((t) => t.id !== tab.id);
      const wasActive = store.activeTabId() === tab.id;

      if (!wasActive) {
        removeTabById(tab.id);

        return;
      }

      const nextTab = remainingTabs[Math.min(idx, remainingTabs.length - 1)];

      if (!nextTab || !(await activateTabRoute(nextTab.id))) return;

      const currentTab = getTabById(tab.id);

      if (!currentTab) return;

      removeTabById(currentTab.id);
    };

    const closeTab = (tabId: string): void => void closeTabRoute(tabId);

    const backToPrev = async (): Promise<void> => {
      const activeTab = store.activeTab();

      if (!activeTab) return;

      const history = activeTab.history;

      if (history.length < 2) return;

      const updatedHistory = history.slice(0, -1);
      const nextTabBase = updatedHistory[updatedHistory.length - 1];

      if (!nextTabBase) return;

      const updatedTab = createTab(activeTab.id, nextTabBase, [...updatedHistory]);

      const navigated = await navRouteService.navigateByUrl(updatedTab.routeUrl, {
        intent: NavIntent.InPlace,
        tabId: activeTab.id,
      });

      if (!navigated) return;
      if (store.activeTabId() !== activeTab.id) return;

      replaceTab(activeTab.id, updatedTab);
    };

    const syncTabQuery = async (queryParams: QueryParams): Promise<void> => {
      const activeTab = store.activeTab();

      if (!activeTab) return;

      const newUrl = navRouteService.replaceParams(activeTab.routeUrl, queryParams);

      if (!newUrl) return;

      const newKeyObj = navRouteService.createKey(newUrl);

      if (!newKeyObj) return;

      const nextKey = activeTab.isRoot ? activeTab.key : navRouteService.getKeyString(newKeyObj);
      const base: TabBase = {
        key: nextKey,
        isRoot: activeTab.isRoot,
        title: activeTab.title,
        ...(activeTab.titleParams ? { titleParams: activeTab.titleParams } : {}),
        routeUrl: navRouteService.getKeyUrl(newKeyObj),
      };

      const navigated = await navRouteService.navigateByUrl(base.routeUrl, {
        intent: NavIntent.InPlace,
        tabId: activeTab.id,
      });

      if (!navigated) return;
      if (store.activeTabId() !== activeTab.id || !getTabById(activeTab.id)) return;

      updateTabRoute(activeTab, base);
    };

    const syncActiveTabRoute = (context: NavContext): void => {
      const base = createTabBase();

      if (!base || isExcludedPath(base.routeUrl)) return;

      const targetTab = context.tabId ? getTabById(context.tabId) : null;

      if (targetTab) {
        if (targetTab.routeUrl !== base.routeUrl) updateTabRoute(targetTab, base);
        if (store.activeTabId() !== targetTab.id) setActiveTabId(targetTab.id);

        return;
      }

      // Explicit new-tab navigation is finalized by openNewTab after NavigationEnd.
      if (context.intent === NavIntent.TabSwitch) return;

      const exactTab = getTabByKey(base.key);

      if (exactTab) {
        if (context.tabId) navCacheService.delete(exactTab.id);
        if (exactTab.routeUrl !== base.routeUrl) updateTabRoute(exactTab, base);
        if (store.activeTabId() !== exactTab.id) setActiveTabId(exactTab.id);

        return;
      }

      const activeTab = store.activeTab();

      if (!activeTab) return;

      const activeKeyObj = navRouteService.createKey(activeTab.routeUrl);
      const nextKeyObj = navRouteService.createKey(base.routeUrl);
      const staysOnPath = !!activeKeyObj && !!nextKeyObj && activeKeyObj.path === nextKeyObj.path;

      if (!staysOnPath) navCacheService.delete(activeTab.id);

      if (staysOnPath) {
        updateTabRoute(activeTab, base);
      } else {
        replaceTab(activeTab.id, createTab(activeTab.id, base, [base]));
      }
    };

    const setupTabRouteSync = (): void => {
      navEndSubscription?.unsubscribe();

      navEndSubscription = router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          navRouteService.captureContext(event);

          return;
        }

        if (!(event instanceof NavigationEnd)) return;

        syncActiveTabRoute(navRouteService.getCurrentContext());
        navRouteService.clearContext();
      });
    };

    const cleanup = (): void => {
      navEndSubscription?.unsubscribe();
      navEndSubscription = null;
      navRouteService.clearContext();
    };

    const initialize = async (id: number): Promise<void> => {
      const rawTabs = session.getJSON<unknown>(SESSION_STORAGE_KEYS.nav.tabs);
      const rawActiveTabId = session.getJSON<unknown>(SESSION_STORAGE_KEYS.nav.activeTabId);
      const seenIds = new Set<string>();
      const storedTabs = Array.isArray(rawTabs)
        ? rawTabs
            .filter(isTab)
            .filter((t) => {
              if (seenIds.has(t.id) || !navRouteService.matchesTabUrl(t, t.routeUrl)) {
                return false;
              }

              seenIds.add(t.id);

              return true;
            })
            .slice(0, MAX_TABS)
        : [];
      const storedActiveTabId = typeof rawActiveTabId === 'string' ? rawActiveTabId : '';
      const storedActiveTab = storedTabs.find((t) => t.id === storedActiveTabId) ?? null;

      setTabs(storedTabs);
      setActiveTabId('');

      const isCurrent = (): boolean => id === lifecycleId && authStore.isAuthenticated();
      const openCurrentRoute = async (): Promise<void> => {
        const url = navRouteService.normalizeUrl(router.url);

        if (!url) return openLandingTab();
        if (isExcludedPath(url)) return;

        const preferredTab =
          storedActiveTab && navRouteService.matchesTabUrl(storedActiveTab, url)
            ? getTabById(storedActiveTab.id)
            : getTabByUrl(url);

        if (preferredTab) {
          await activateTabRoute(preferredTab.id, preferredTab.isRoot ? url : undefined);

          return;
        }

        if (store.maxTabsReached()) {
          const discarded = getTabById(storedActiveTabId) ?? store.tabs().at(-1);

          if (discarded) removeTabById(discarded.id);
        }

        await openNewTab(url);
      };

      const browserUrl =
        typeof window === 'undefined'
          ? null
          : navRouteService.normalizeUrl(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            );
      const browserPath = browserUrl ? navRouteService.createKey(browserUrl)?.path : null;

      if (browserUrl && browserPath !== AppPath.Login) {
        if (!router.navigated) {
          await new Promise<void>((resolve) => {
            router.events
              .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                take(1),
              )
              .subscribe(() => resolve());
          });
        }

        if (isCurrent()) await openCurrentRoute();

        return;
      }

      const candidates = [
        ...(storedActiveTab ? [storedActiveTab] : []),
        ...storedTabs.filter((t) => t.id !== storedActiveTab?.id),
      ];

      for (const candidate of candidates) {
        if (!isCurrent()) return;

        const navigated = await navRouteService.navigateByUrl(candidate.routeUrl, {
          intent: NavIntent.TabSwitch,
          tabId: candidate.id,
        });

        if (!isCurrent()) return;

        const currentTab = getTabById(candidate.id);

        if (navigated && currentTab && navRouteService.matchesTabUrl(currentTab, router.url)) {
          setActiveTabId(currentTab.id);

          return;
        }

        if (currentTab) removeTabById(currentTab.id);
      }

      if (isCurrent()) openLandingTab();
    };

    const activate = (): void => {
      patchState(store, {
        menuItems: createMenuItems({
          counts: {
            inbox: store.inboxCount,
            conversations: store.conversationsCount,
          },
          links: [],
        }),
      });

      setupTabRouteSync();
      void initialize(++lifecycleId);
    };

    // Destroys cached route components on logout so they're never reattached for the next user.
    const deactivate = (): void => {
      lifecycleId += 1;
      cleanup();
      navCacheService.clear();
      patchState(store, {
        tabs: [],
        activeTabId: '',
        tabLimitAttempts: 0,
        menuItems: [],
        inboxCount: 0,
        conversationsCount: 0,
      });
    };

    const setUserId = (nextUserId: number | null): void => {
      if (nextUserId === null) {
        userId = null;
        deactivate();

        return;
      }

      if (userId !== null && userId !== nextUserId) {
        deactivate();
        session.remove(SESSION_STORAGE_KEYS.nav.tabs);
        session.remove(SESSION_STORAGE_KEYS.nav.activeTabId);
      }

      userId = nextUserId;
      activate();
    };

    return {
      hasTabKey,
      getTabById,
      activateTab,
      openTab,
      closeTab,
      backToPrev,
      syncTabQuery,

      _setInboxCount: (count: number): void => {
        patchState(store, { inboxCount: count });
      },
      _setConversationsCount: (count: number): void => {
        patchState(store, { conversationsCount: count });
      },

      _cleanup: cleanup,
      _setUserId: setUserId,
    };
  }),

  withHooks({
    onInit(store): void {
      const authStore = inject(AuthStore);

      toObservable(authStore.user)
        .pipe(
          map((user) => user?.id ?? null),
          distinctUntilChanged(),
          takeUntilDestroyed(),
        )
        .subscribe((id) => store._setUserId(id));
    },

    onDestroy(store): void {
      store._cleanup();
    },
  }),
);
